import React, { useEffect, useRef, useState } from "react";

const GREEN = "#7DD856";
const DARK = "#3F3F3F";
const R = 88;
const STROKE = 34;
const C = 2 * Math.PI * R;
const GREEN_ARC = C * (262 / 360);   // the logo's green sweep
const LOAD_ARC = C * 0.2;            // short segment while it "loads"
const REST_ANGLE = -104;             // where the logo's green arc sits

/**
 * The brand mark: the ring spins like a loader, settles into the logo, and from then on the
 * green arc follows the scroll — down turns it clockwise, up turns it anticlockwise.
 */
export const LogoRing = ({ size = 280, glyph = "", label = "ReHealthMe", spinSeconds = 2.5 }) => {
    const [settled, setSettled] = useState(false);
    const group = useRef(null);
    const spin = Math.min(Math.max(Number(spinSeconds) || 2.5, 0.4), 12);
    const reduced = typeof window !== "undefined"
        && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    useEffect(() => {
        if (reduced) return setSettled(true);
        const timer = setTimeout(() => setSettled(true), Math.min(spin * 1500, 5000));
        return () => clearTimeout(timer);
    }, [reduced, spin]);

    useEffect(() => {
        if (!settled || reduced) return undefined;
        let frame = 0;
        const paint = () => {
            if (group.current) {
                group.current.style.transform =
                    `rotate(${REST_ANGLE + window.scrollY * 0.36}deg)`;
            }
        };
        const onScroll = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(paint);
        };
        paint();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => { window.removeEventListener("scroll", onScroll); cancelAnimationFrame(frame); };
    }, [settled, reduced]);

    return (
        <div className="relative" style={{ width: size, height: size, "--ring-spin": `${spin}s` }}
            data-testid="logo-ring" aria-label={label} role="img">
            <svg viewBox="0 0 240 240" width={size} height={size} className="block">
                <circle cx="120" cy="120" r={R} fill="none" stroke={DARK} strokeWidth={STROKE} />
                <g ref={group} className={`logo-ring__arc ${settled ? "is-settled" : "is-loading"}`}
                    style={{ transformOrigin: "120px 120px",
                        transform: settled ? `rotate(${REST_ANGLE}deg)` : undefined }}>
                    <circle cx="120" cy="120" r={R} fill="none" stroke={GREEN} strokeWidth={STROKE}
                        strokeLinecap="round"
                        strokeDasharray={settled ? `${GREEN_ARC} ${C}` : `${LOAD_ARC} ${C}`} />
                </g>
                <circle cx="120" cy="120" r={R - STROKE / 2 + 1} fill="#FFFFFF" />
            </svg>
            {glyph && (
                <img src={glyph} alt="" data-testid="logo-ring-glyph"
                    className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transition-all duration-700 ${
                        settled ? "opacity-100 scale-100" : "opacity-0 scale-90"}`}
                    style={{ width: size * 0.44, height: size * 0.44, objectFit: "contain" }} />
            )}
        </div>
    );
};

export default LogoRing;
